import { Box, InputLabel, Typography } from '@mui/material'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { useEffect, useRef, useState } from 'react'
import { isNotEmpty } from '../../utils/helpers'
import formMaxCharacters from '../../utils/formMaxCharacters'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'

const OfoqTextEditor = ({
  name,
  defaultValue,
  refState,
  required,
  customPlaceHolder,
  disableMaximumCharacters,
  customStyle,
  maxCharacters = formMaxCharacters.textEditor,
  contentLanguage,
}) => {
  const { translations, language } = UseTranslation()
  const [editorCount, setEditorCount] = useState('')
  const inputTitle = name?.split('_').join(' ')
  const placeHolder = customPlaceHolder || name?.split('_').join(' ')

  const editorRef = useRef(null)
  const [data, setData] = useState(defaultValue || '')

  useEffect(() => {
    setData(defaultValue || '')
    setEditorCount((defaultValue || '').length)
  }, [defaultValue])

  const inputHandler = (_, editor) => {
    const newData = editor.getData()
    const newDataLength = newData.length
    if (newDataLength <= maxCharacters) {
      setEditorCount(newDataLength)
      setData(newData)
      refState(newData)
    } else {
      editorRef.current.setData(data)
      setEditorCount(newDataLength)
    }
  }
  const resolvedLanguage = (contentLanguage || language || '').toLowerCase()
  const isArabic = resolvedLanguage === 'ar'

  return (
    <Box sx={[styles.root, customStyle && customStyle]}>
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}
      >
        {inputTitle}
        {required && <TeamSnap style={styles.snapIcon} />}

      </InputLabel>

      <CKEditor
        editor={ClassicEditor}
        data={defaultValue || ''}
        config={{
          highlight: {
            options: [
              {
                model: 'orangePen',
                class: 'pen-orange',
                title: 'Orange',
                color: '#e59536',
                type: 'pen',
              },
              {
                model: 'lightBluePen',
                class: 'pen-light-blue',
                title: 'Light blue',
                color: '#30caf7',
                type: 'pen',
              },
              {
                model: 'darkBluePen',
                class: 'pen-dark-blue',
                title: 'Dark blue',
                color: '#3982a4',
                type: 'pen',
              },
              {
                model: 'greenPen',
                class: 'pen-green',
                title: 'Green',
                color: '#488f80',
                type: 'pen',
              },
              {
                model: 'grayPen',
                class: 'pen-gray',
                title: 'Dark gray',
                color: '#4d4d4c',
                type: 'pen',
              },
            ],
          },
          toolbar: {
            items: ['heading', 'bold', 'italic', 'underline', 'numberedList', 'bulletedList'],
          },
          language: {
            content: isArabic ? 'ar' : 'en',
          },
          typing: {
            direction: isArabic ? 'rtl' : 'ltr',
            spellCheck: !isArabic,
          },
        }}
        onChange={inputHandler}
        onReady={(editor) => {
          editorRef.current = editor
        }}
      />

      {!isNotEmpty(data) && (
        <Typography sx={styles.customPlaceHolder} variant="body1">
          {placeHolder}
        </Typography>
      )}
      <Box sx={disableMaximumCharacters ? styles.hideMaximumCharacters : styles.countStyle}>
        {translations?.['Maximum characters']}
        {`  ${maxCharacters}`}
      </Box>

      {editorCount >= maxCharacters && (
      <Box sx={styles.overCountStyle}>
        {translations?.['You have reached the maximum characters']}
      </Box>
      )}
    </Box>
  )
}

const styles = {
  root: {
    position: 'relative',
    marginBottom: { xs: '15px', sm: '30px' },

    '& .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar': {
      padding: { xs: '0px', sm: '10px' },
      border: '2px solid',
      borderColor: '#E2E5EA',
      borderTopRightRadius: '15px',
      borderTopLeftRadius: '15px',
      borderWidth: '2px !important',
      backgroundColor: '#fafbfc',
    },

    '& .ck-rounded-corners .ck.ck-editor__main>.ck-editor__editable, .ck.ck-editor__main>.ck-editor__editable.ck-rounded-corners':
      {
        border: '2px solid',
        borderColor: '#E2E5EA',
        borderBottomRightRadius: '15px',
        borderBottomLeftRadius: '15px',
        borderWidth: '2px !important',
        minHeight: '100px',
        boxShadow: 'unset',
        padding: { xs: '-10px 15px', sm: '15px 20px' },
        backgroundColor: '#fafbfc',
        fontFamily: 'Avenir',
        fontSize: { xs: '14px', sm: '16px' },

      },

    '& .ck.ck-dropdown .ck-button.ck-dropdown__button': {
      fontSize: '14px',
      fontWeight: '700',
    },

    '& .ck-content ul': {
      padding: '0 20px',
    },

    '& .ck.ck-dropdown.ck-heading-dropdown .ck-dropdown__button .ck-button__label': {
      width: '6em',
    },

    '& .ck.ck-toolbar>.ck-toolbar__items>:not(.ck-toolbar__line-break)': {
      color: '#98a9bc',
    },

    '& div[tabindex="0"]': {
      position: 'relative',
      padding: '0 20px',
      maxWidth: '95%',
    },
  },
  customPlaceHolder: {
    top: { xs: 100, sm: 135 },
    left: 25,
    color: '#818181',
    position: 'absolute',
    textTransform: 'capitalize',
    pointerEvents: 'none',
    fontSize: '14px',
  },
  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'common.black',
    fontSize: '14px',
    width: 'max-content',
    paddingInlineEnd: '18px',
    marginBottom: '15px',
  },
  snapIcon: {
    marginInlineStart: '8px',
  },
  countStyle: {
    position: 'absolute',
    right: '20px /* @noflip */',
    top: '55px',
    padding: '4px',
    borderRadius: '7px',
    color: '#9ca2a8',
    fontSize: '12px',
    display: { xs: 'none', sm: 'block' },
  },
  hideMaximumCharacters: {
    display: 'none',
  },
  overCountStyle: {
    marginTop: '10px',
    padding: '4px',
    fontSize: '12px',
    borderRadius: '7px',
    color: 'error.main',
  },
}

export default OfoqTextEditor
